import * as Sentry from '@sentry/react';

export function publishEvent(
  eventName: string,
  eventTags: { [key: string]: number | string | boolean | bigint | symbol | null | undefined }
) {
  Sentry.captureEvent({
    message: eventName,
    tags: eventTags,
    level: 'info'
  });
}

export function publishTags(tags: { [key: string]: number | string | boolean | bigint | symbol | null | undefined }) {
  Object.entries(tags).forEach(([key, value]) => {
    if (key && value) {
      Sentry.setTag('p2g-' + key, value);
    }
  });
}

export function captureException(e: unknown, message: string, tags: { [key: string]: string }) {
  console.error(message, e);
  Sentry.captureException(e, {
    tags: tags
  });
}

export function capturePromiseException(e: unknown, tags?: { [key: string]: string }) {
  Sentry.captureException(e, tags ? { tags } : undefined);
}

/**
 * Steps in a payment attempt, used as the breadcrumb message so a payment's
 * timeline can be read in a Sentry replay/issue.
 */
export type PaymentStep =
  | 'init'
  | 'instance-ready'
  | 'method-selected'
  | 'submit-start'
  | 'submit-success'
  | 'submit-fail';

/**
 * Adds a breadcrumb under the shared `payment` category so every provider
 * contributes to one filterable timeline. Keep `data` free of PII.
 */
export function addPaymentBreadcrumb(step: PaymentStep, data?: { [key: string]: unknown }) {
  Sentry.addBreadcrumb({ category: 'payment', message: step, data, level: 'info' });
}

/**
 * Tags the current scope so payment issues/replays are filterable by provider,
 * method and order. Mirrors the `p2g-` tag convention used by publishTags.
 */
export function setPaymentContext(ctx: { provider: string; method?: string; orderId?: string | null }) {
  // Always write all three tags so a previous payment's method/order can't linger
  // on later errors/replays in the SPA. Absent values use the 'unknown' sentinel.
  Sentry.setTag('p2g-payment-provider', ctx.provider);
  Sentry.setTag('p2g-payment-method', ctx.method ?? 'unknown');
  Sentry.setTag('p2g-payment-order-id', ctx.orderId ?? 'unknown');
}

export function captureWarning(message: string, tags: { [key: string]: string }, params: unknown[] = []) {
  console.warn(message, ...params);
  Sentry.captureMessage(message, {
    tags: tags,
    level: 'warning'
  });
}

export function endReplay() {
  try {
    const replay = Sentry.getReplay();
    if (replay) {
      replay.stop();
    }
  } catch (e) {
    console.error('Failed to stop Sentry replay', e);
  }
}
